// Contractor Access, Stage 5 piece 6. Words: OBSERVATIONS_SCAN_WORDS.md rev 3, section 2 (approved by
// Robert 2026-10-12, program lines and questions "as written", to be tuned after the field test).
// Mockup: MOCKUP_CONTRACTOR_ACCESS.html rev 3.
//
// A site orientation with a sponsor. Any label gets a contractor in; the asset is not the gate.
// 1. Read the program lines (plus this equipment when the label has data).
// 2. The check: 5 questions, all right to pass. A miss shows the line again; the question comes back at the end.
// 3. Who you are and who brought you in: the sponsor's company email (domains from Program Settings) and job days.
// 4. Wait. The sponsor gets one email with a single-use link: Confirm or Not Mine. 4 hours, then it expires.
//    Silence never confirms.
// Then one daily check email to the sponsor each job day at 3 PM until the job ends. Yes emails the ESP Manager.
// Two job days in a row with no answer tell the site's Alerts contacts once.
//
// Day 1 of the job is the day the sponsor confirms (the mockup's timeline). A confirm after 3 PM starts the
// emails on day 2.
//
// Pure: data in, data out. Shared with the server and unit tested.

import { dayDiff, programContactsFor, siteContactsFor, siteNameOf, type AlertWorld, type PlannedAlert } from "./alerts";
import type { EmailRequest, Recipient } from "./email";

// ---- The program, version 1 ----

export interface CheckQuestion {
  text: string;
  options: string[];
  right: number;
  /** The program line to read again on a miss (index into lines). */
  line: number;
}

export interface ContractorProgram {
  version: string;
  lines: string[];
  questions: CheckQuestion[];
}

export const CONTRACTOR_PROGRAM_V1: ContractorProgram = {
  version: "V1",
  lines: [
    "Contractors are not exempt. You follow the same programs Alltech employees follow: LOTO, the Electrical Safety Program, arc flash and PPE.",
    "Only qualified electrical workers do electrical work. Your employer documents your qualification (NFPA 70E 110.2).",
    "De-energize first. Every lockout uses the Alltech LOTO Permit. Prove it dead: test your meter, test dead, test your meter again.",
    "Energized work at 50 V or more needs an approved Energized Electrical Work Permit. Production, schedule and cost are never reasons.",
    "Bring your own PPE. Shock: Class 0 rubber gloves with leather protectors at 50 V or more. Arc flash: arc-rated PPE rated at or above the incident energy on the label.",
    "A RED magnet on the label, or 12 cal/cm² or more: no energized work. Isolate upstream.",
    "Stay outside the limited approach boundary unless you are qualified for the task. Keep others out.",
    "Any Alltech QEW can stop your work. Stopping work is never a violation.",
    "Report any injury or near miss to your sponsor right away.",
  ],
  questions: [
    {
      text: "Before electrical work, what comes first?",
      options: ["Start if the job is short.", "De-energize, lock out on the Alltech LOTO Permit, prove it dead.", "Ask an operator to switch it off."],
      right: 1,
      line: 2,
    },
    {
      text: "Energized work at 50 V or more needs:",
      options: ["An approved Energized Electrical Work Permit.", "A phone OK from your supervisor.", "Nothing, if you wear PPE."],
      right: 0,
      line: 3,
    },
    {
      text: "A RED magnet on the label means:",
      options: ["More PPE is enough.", "No energized work. Isolate upstream.", "The label is out of date."],
      right: 1,
      line: 5,
    },
    {
      text: "Who can stop your work?",
      options: ["Only your own supervisor.", "Only the plant manager.", "Any Alltech QEW."],
      right: 2,
      line: 7,
    },
    {
      text: "Your arc flash PPE must be:",
      options: ["Borrowed from Alltech.", "Your own, rated at or above the incident energy on the label.", "Any flame-resistant shirt."],
      right: 1,
      line: 4,
    },
  ],
};

/** The sponsor ticks each one before Confirm. */
export const SPONSOR_COMMITMENTS = [
  "I brought this contractor in for this work.",
  "I will brief them on site hazards, including equipment with no QR label.",
  "I will check on their work and stop it if it is unsafe.",
  "I will answer the daily follow-up until the job is done.",
] as const;

export const SPONSOR_WAIT_HOURS = 4;
export const MAX_JOB_DAYS = 30;
/** One start per phone every 5 minutes. */
export const START_GAP_MINUTES = 5;
/** The daily check goes at 3 PM site time. */
export const FOLLOW_UP_HOUR = 15;
export const CONTRACTOR_REASON = "Contractor visit";
export const NOT_COMPANY_EMAIL = "That is not a company email. Ask the person who brought you in for their work email.";
export const NO_DOMAINS = "Company email domains are not set yet. Ask the ESP Manager to add them in Program Settings.";
export const START_TOO_SOON = "You just sent one. Try again in a few minutes.";
export const PRACTICE_LINE = "Practice. Nothing was sent.";
export const NOTE_MAX = 300;

// ---- The check ----

export interface CheckState {
  /** Questions still to get right, in order. A miss goes to the back. */
  queue: number[];
  /** The right answer once given, per question. */
  answers: (number | null)[];
  misses: number;
  /** The option picked on the current question. */
  pick: number | null;
  /** The question just missed, showing its line. */
  missed: number | null;
}

export function checkStart(p: ContractorProgram = CONTRACTOR_PROGRAM_V1): CheckState {
  return { queue: p.questions.map((_, i) => i), answers: p.questions.map(() => null), misses: 0, pick: null, missed: null };
}

export function checkPick(s: CheckState, option: number): CheckState {
  return s.missed === null ? { ...s, pick: option } : s;
}

/** Next on the current question, or Got It on a miss. */
export function checkNext(s: CheckState, p: ContractorProgram = CONTRACTOR_PROGRAM_V1): CheckState {
  const q = s.queue[0];
  if (q === undefined) return s;
  if (s.missed !== null) return { ...s, queue: [...s.queue.slice(1), q], missed: null, pick: null };
  if (s.pick === null) return s;
  if (s.pick === p.questions[q]!.right) {
    const answers = [...s.answers];
    answers[q] = s.pick;
    return { ...s, queue: s.queue.slice(1), answers, pick: null };
  }
  return { ...s, misses: s.misses + 1, missed: q };
}

export function checkPassed(s: CheckState): boolean {
  return s.queue.length === 0;
}

/** "Question 3 of 5": the questions already right, plus this one. */
export function checkPosition(s: CheckState): number {
  return s.answers.filter((a) => a !== null).length + 1;
}

/** Server side: every question answered right. */
export function answersPass(answers: unknown, p: ContractorProgram = CONTRACTOR_PROGRAM_V1): boolean {
  return Array.isArray(answers) && answers.length === p.questions.length && p.questions.every((q, i) => answers[i] === q.right);
}

// ---- Who you are ----

export interface ContractorDetails {
  name: string;
  company: string;
  phone: string;
  supervisor: string;
  sponsorEmail: string;
  jobDays: number;
}

export const EMPTY_DETAILS: ContractorDetails = { name: "", company: "", phone: "", supervisor: "", sponsorEmail: "", jobDays: 1 };

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DOMAIN = /^[a-z0-9-]+(\.[a-z0-9-]+)+$/;

/** "alltech.com, ridley.com" or one per line. Lower case, no @, no duplicates. */
export function parseDomains(text: string): string[] {
  const out: string[] = [];
  for (const raw of text.split(/[\s,;]+/)) {
    const d = raw.trim().toLowerCase().replace(/^@/, "");
    if (d && !out.includes(d)) out.push(d);
  }
  return out;
}

export function domainsProblem(list: readonly string[]): string | null {
  const bad = list.find((d) => !DOMAIN.test(d));
  if (bad) return `${bad} is not a domain. Write it like alltech.com.`;
  if (list.length > 30) return "Keep it to 30 domains.";
  return null;
}

export function domainOf(email: string): string {
  const at = email.trim().toLowerCase().lastIndexOf("@");
  return at < 0 ? "" : email.trim().toLowerCase().slice(at + 1);
}

/** The domain itself or a subdomain of it: j.smith@us.alltech.com passes for alltech.com. */
export function isCompanyEmail(email: string, domains: readonly string[]): boolean {
  if (!EMAIL.test(email.trim())) return false;
  const d = domainOf(email);
  return domains.some((x) => d === x || d.endsWith(`.${x}`));
}

export function digitsOf(phone: string): string {
  return phone.replace(/\D/g, "");
}

export function detailsProblem(d: ContractorDetails, domains: readonly string[]): string | null {
  if (!d.name.trim()) return "Enter your name.";
  if (!d.company.trim()) return "Enter your company.";
  if (digitsOf(d.phone).length < 10) return "Enter a phone number your sponsor can call.";
  if (!d.supervisor.trim()) return "Enter your supervisor at your company.";
  if (!d.sponsorEmail.trim()) return "Enter the company email of the person who brought you in.";
  if (!EMAIL.test(d.sponsorEmail.trim())) return "That email is not complete.";
  if (!domains.length) return NO_DOMAINS;
  if (!isCompanyEmail(d.sponsorEmail, domains)) return NOT_COMPANY_EMAIL;
  if (!Number.isInteger(d.jobDays) || d.jobDays < 1 || d.jobDays > MAX_JOB_DAYS) return `Pick 1 to ${MAX_JOB_DAYS} days.`;
  return null;
}

export function cleanDetails(raw: Record<string, unknown>): ContractorDetails {
  const t = (v: unknown, max: number) => (typeof v === "string" ? v.trim().replace(/\s+/g, " ").slice(0, max) : "");
  const days = typeof raw.jobDays === "number" ? raw.jobDays : Number(raw.jobDays);
  return {
    name: t(raw.name, 80),
    company: t(raw.company, 80),
    phone: t(raw.phone, 30),
    supervisor: t(raw.supervisor, 80),
    sponsorEmail: t(raw.sponsorEmail, 120).toLowerCase(),
    jobDays: Number.isFinite(days) ? Math.trunc(days) : 0,
  };
}

/** True when the last start from this phone is inside the gap. */
export function startTooSoon(lastIso: string | null, nowIso: string): boolean {
  if (!lastIso) return false;
  return Date.parse(nowIso) - Date.parse(lastIso) < START_GAP_MINUTES * 60_000;
}

// ---- The visit: companies/{c}/contractorVisits/CV-#### ----

export type VisitStatus = "Waiting" | "On Site" | "Done" | "Not Mine" | "Expired";
export type FollowUpAnswer = "no" | "yes" | null;

export interface FollowUp {
  day: number;
  /** The job day's date, site time. */
  on: string;
  sentAt: string;
  tokenHash: string;
  answer: FollowUpAnswer;
  note: string;
  at: string | null;
}

export interface VisitRow {
  visitId: string;
  siteNumber: string;
  scannedAssetId: string;
  contractor: { name: string; company: string; phone: string; supervisor: string };
  check: { version: string; passedAt: string; misses: number };
  jobDays: number;
  startOn: string | null;
  endsOn: string | null;
  sponsorEmail: string;
  sponsorName: string;
  commitments: string[];
  confirmedAt: string | null;
  status: VisitStatus;
  expiresAt: string;
  endedAt: string | null;
  endReason: string;
  followUps: FollowUp[];
  missedAlerts: number[];
  seenAt: string | null;
  createdOn: string;
  createdAt: string;
}

const s = (v: unknown) => (typeof v === "string" ? v : "");
const sn = (v: unknown) => (typeof v === "string" && v ? v : null);
const STATUSES: VisitStatus[] = ["Waiting", "On Site", "Done", "Not Mine", "Expired"];

export function visitRowOf(id: string, d: Record<string, unknown>): VisitRow {
  const c = (d.contractor ?? {}) as Record<string, unknown>;
  const k = (d.check ?? {}) as Record<string, unknown>;
  const fu = Array.isArray(d.followUps) ? (d.followUps as Record<string, unknown>[]) : [];
  return {
    visitId: id,
    siteNumber: s(d.siteNumber),
    scannedAssetId: s(d.scannedAssetId),
    contractor: { name: s(c.name), company: s(c.company), phone: s(c.phone), supervisor: s(c.supervisor) },
    check: { version: s(k.version), passedAt: s(k.passedAt), misses: typeof k.misses === "number" ? k.misses : 0 },
    jobDays: typeof d.jobDays === "number" ? d.jobDays : 1,
    startOn: sn(d.startOn),
    endsOn: sn(d.endsOn),
    sponsorEmail: s(d.sponsorEmail),
    sponsorName: s(d.sponsorName),
    commitments: Array.isArray(d.commitments) ? d.commitments.filter((x): x is string => typeof x === "string") : [],
    confirmedAt: sn(d.confirmedAt),
    status: STATUSES.includes(d.status as VisitStatus) ? (d.status as VisitStatus) : "Waiting",
    expiresAt: s(d.expiresAt),
    endedAt: sn(d.endedAt),
    endReason: s(d.endReason),
    followUps: fu.map((f) => ({
      day: typeof f.day === "number" ? f.day : 0,
      on: s(f.on),
      sentAt: s(f.sentAt),
      tokenHash: s(f.tokenHash),
      answer: f.answer === "yes" || f.answer === "no" ? f.answer : null,
      note: s(f.note),
      at: sn(f.at),
    })),
    missedAlerts: Array.isArray(d.missedAlerts) ? d.missedAlerts.filter((x): x is number => typeof x === "number") : [],
    seenAt: sn(d.seenAt),
    createdOn: s(d.createdOn),
    createdAt: s(d.createdAt),
  };
}

/** Waiting past 4 hours reads Expired, before any job writes it. */
export function visitStatusOf(v: Pick<VisitRow, "status" | "expiresAt">, nowIso: string): VisitStatus {
  if (v.status === "Waiting" && v.expiresAt && nowIso >= v.expiresAt) return "Expired";
  return v.status;
}

export function expiresAtOf(createdAt: string): string {
  return new Date(Date.parse(createdAt) + SPONSOR_WAIT_HOURS * 3_600_000).toISOString();
}

export function addDays(iso: string, n: number): string {
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** The last job day: day 1 is the start. */
export function endsOnOf(startOn: string, jobDays: number): string {
  return addDays(startOn, jobDays - 1);
}

/** Day 1 on the start date. */
export function jobDayOf(startOn: string, today: string): number {
  return dayDiff(startOn, today) + 1;
}

/** Yes answers after the last Mark Seen. */
export function unseenYes(v: Pick<VisitRow, "followUps" | "seenAt">): FollowUp[] {
  return v.followUps.filter((f) => f.answer === "yes" && (!v.seenAt || (f.at ?? "") > v.seenAt));
}

/** Amber on the tile: waiting on a sponsor, or a Yes not seen yet. */
export function visitNeedsLook(v: VisitRow, nowIso: string): boolean {
  return visitStatusOf(v, nowIso) === "Waiting" || unseenYes(v).length > 0;
}

/** What the 3 PM job does for one visit today. */
export interface DailyPlan {
  /** The visit ends: today is past the last job day. */
  done: boolean;
  /** Send today's check for this job day. */
  sendDay: number | null;
  /** The first day of a run of 2 or more unanswered days, not told yet. */
  missedFrom: number | null;
}

export function dailyPlanOf(v: VisitRow, today: string): DailyPlan {
  const none: DailyPlan = { done: false, sendDay: null, missedFrom: null };
  if (v.status !== "On Site" || !v.startOn || !v.endsOn) return none;
  if (today > v.endsOn) return { ...none, done: true };
  const day = jobDayOf(v.startOn, today);
  const sendDay = day >= 1 && !v.followUps.some((f) => f.day === day) ? day : null;
  // The trailing run of sent, unanswered days before today.
  const before = v.followUps.filter((f) => f.day < day).sort((a, b) => b.day - a.day);
  const run: number[] = [];
  for (const f of before) {
    if (f.answer !== null) break;
    if (run.length && f.day !== run[run.length - 1]! - 1) break;
    run.push(f.day);
  }
  const from = run.length >= 2 ? run[run.length - 1]! : null;
  return { done: false, sendDay, missedFrom: from !== null && !v.missedAlerts.includes(from) ? from : null };
}

// ---- Who is told ----

export function confirmProblem(name: string, commitments: readonly unknown[]): string | null {
  if (!name.trim()) return "Type your name. It is stamped on the record.";
  if (!SPONSOR_COMMITMENTS.every((c) => commitments.includes(c))) return "Tick each commitment to confirm.";
  return null;
}

/** "Mike Ortiz · Bluegrass Electric" */
export function whoLine(v: Pick<VisitRow, "contractor">): string {
  return [v.contractor.name, v.contractor.company].filter(Boolean).join(" · ");
}

export function usShort(iso: string | null): string {
  if (!iso) return "";
  const [, m, d] = iso.split("-");
  return m && d ? `${m}/${d}` : iso;
}

export function clockText(iso: string, timeZone = "America/New_York"): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat("en-US", { timeZone, hour: "numeric", minute: "2-digit" }).format(d);
}

/** Link paths. The code is the key; the visit keeps only its hash. */
export function sponsorLinkPath(visitId: string, code: string, companyId: string, day: number | null = null): string {
  return `?cv=${encodeURIComponent(visitId)}${day ? `&d=${day}` : ""}&k=${encodeURIComponent(code)}&co=${encodeURIComponent(companyId)}`;
}

export function sponsorParamsFrom(search: string): { visitId: string; day: number | null; code: string; company: string } | null {
  const p = new URLSearchParams(search);
  const visitId = (p.get("cv") ?? "").trim();
  if (!/^CV-\d{4,}$/.test(visitId)) return null;
  const d = Number(p.get("d") ?? "");
  return { visitId, day: Number.isInteger(d) && d > 0 ? d : null, code: (p.get("k") ?? "").trim(), company: (p.get("co") ?? "alltech").trim() || "alltech" };
}

const signOf = (world: AlertWorld) =>
  world.companyName ? `Sent by SafeTag for the ${world.companyName} Electrical Safety Program.` : "Sent by SafeTag for the Electrical Safety Program.";

function mail(v: VisitRow, world: AlertWorld, m: Omit<EmailRequest, "siteNumber" | "siteName" | "companyName" | "reason" | "recordId">): EmailRequest {
  return { ...m, siteNumber: v.siteNumber, siteName: siteNameOf(world, v.siteNumber), companyName: world.companyName, reason: CONTRACTOR_REASON, recordId: v.visitId };
}

function sponsorTo(v: VisitRow): Recipient[] {
  return EMAIL.test(v.sponsorEmail) ? [{ name: v.sponsorName || v.sponsorEmail, email: v.sponsorEmail }] : [];
}

/** The Confirm email to the sponsor. */
export function confirmMailOf(v: VisitRow, path: string, world: AlertWorld): PlannedAlert | null {
  const to = sponsorTo(v);
  if (!to.length) return null;
  const site = siteNameOf(world, v.siteNumber);
  const e = mail(v, world, {
    to,
    toLabel: v.sponsorEmail,
    subject: `Confirm your contractor · ${v.contractor.company} · ${site}`,
    headline: `${v.contractor.name} of ${v.contractor.company} says you brought them in.`,
    lines: ["They passed the program check. Confirm them, or tap Not Mine if you did not bring them in."],
    rows: [
      { label: "Contractor", value: whoLine(v) },
      { label: "Their supervisor", value: v.contractor.supervisor },
      { label: "Phone", value: v.contractor.phone },
      { label: "Job", value: `${v.jobDays} ${v.jobDays === 1 ? "day" : "days"}` },
      { label: "Program check", value: `Passed 5 of 5 at ${clockText(v.check.passedAt)}` },
      { label: "Expires", value: `${SPONSOR_WAIT_HOURS} hours, ${clockText(v.expiresAt)}` },
    ],
    button: { label: "Review And Confirm", path },
    footer: `You get this because the contractor named you as their sponsor. This link is yours alone and works once. ${signOf(world)}`,
  });
  return { key: `cv-confirm-${v.visitId}`, emails: [e] };
}

/** Not Mine: the site's Alerts contacts hear it once. */
export function notMineAlertOf(v: VisitRow, world: AlertWorld): PlannedAlert | null {
  const to = siteContactsFor(world, v.siteNumber, "alerts");
  if (!to.length) return null;
  const site = siteNameOf(world, v.siteNumber);
  const who = v.sponsorName || v.sponsorEmail;
  const e = mail(v, world, {
    to,
    toLabel: `${site} alerts`,
    subject: `Contractor not confirmed · ${v.contractor.company} · ${site}`,
    headline: `${who} did not bring in ${v.contractor.name} of ${v.contractor.company}.`,
    lines: ["The contractor was told not to start and to find the person who brought them in."],
    rows: [
      { label: "Contractor", value: whoLine(v) },
      { label: "Phone", value: v.contractor.phone },
      { label: "Their supervisor", value: v.contractor.supervisor },
      { label: "Named sponsor", value: v.sponsorEmail },
      { label: "Visit", value: v.visitId },
    ],
    button: { label: "Open The Visit", path: `#/contractors/r/${encodeURIComponent(v.visitId)}` },
    footer: `You get this because you are an Alerts contact at ${site}. ${signOf(world)}`,
  });
  return { key: `cv-notmine-${v.visitId}`, emails: [e] };
}

/** The daily check to the sponsor, one per job day. */
export function dailyMailOf(v: VisitRow, day: number, path: string, world: AlertWorld): PlannedAlert | null {
  const to = sponsorTo(v);
  if (!to.length) return null;
  const site = siteNameOf(world, v.siteNumber);
  const e = mail(v, world, {
    to,
    toLabel: v.sponsorName || v.sponsorEmail,
    subject: `Daily check · ${v.contractor.company} · day ${day} of ${v.jobDays} · ${site}`,
    headline: `Any injury, near miss, damage or unsafe work with ${v.contractor.company} today?`,
    lines: ["One tap. You committed to this check when you confirmed them."],
    rows: [
      { label: "Contractor", value: whoLine(v) },
      { label: "Job", value: `Day ${day} of ${v.jobDays}, ends ${usShort(v.endsOn)}` },
    ],
    button: { label: "Answer Today's Check", path },
    footer: `One email per job day until the job is done. Job Done or Extend are on the page. ${signOf(world)}`,
  });
  return { key: `cv-daily-${v.visitId}-d${day}`, emails: [e] };
}

/** A Yes on the daily check: the ESP Manager (program Observation Alerts) hears it. */
export function yesAlertOf(v: VisitRow, day: number, note: string, world: AlertWorld): PlannedAlert | null {
  const to = programContactsFor(world, "observationAlerts");
  if (!to.length) return null;
  const site = siteNameOf(world, v.siteNumber);
  const e = mail(v, world, {
    to,
    toLabel: "Observation alerts",
    subject: `Contractor daily check: Yes · ${v.contractor.company} · ${site}`,
    headline: `${v.sponsorName || v.sponsorEmail} answered Yes on the daily check.`,
    lines: ["Injury, near miss, damage or unsafe work with this contractor today."],
    rows: [
      { label: "What happened", value: note },
      { label: "Contractor", value: whoLine(v) },
      { label: "Phone", value: v.contractor.phone },
      { label: "Job", value: `Day ${day} of ${v.jobDays}` },
      { label: "Sponsor", value: `${v.sponsorName} · ${v.sponsorEmail}` },
      { label: "Visit", value: v.visitId },
    ],
    button: { label: "Open The Visit", path: `#/contractors/r/${encodeURIComponent(v.visitId)}` },
    footer: `You get this because you are an Observation Alerts contact for the program. ${signOf(world)}`,
  });
  return { key: `cv-yes-${v.visitId}-d${day}`, emails: [e] };
}

/** Two job days in a row with no answer: the site's Alerts contacts hear it once per run. */
export function missedAlertOf(v: VisitRow, fromDay: number, world: AlertWorld): PlannedAlert | null {
  const to = siteContactsFor(world, v.siteNumber, "alerts");
  if (!to.length) return null;
  const site = siteNameOf(world, v.siteNumber);
  const who = v.sponsorName || v.sponsorEmail;
  const e = mail(v, world, {
    to,
    toLabel: `${site} alerts`,
    subject: `Daily check not answered · ${v.contractor.company} · ${site}`,
    headline: `${who} has not answered the daily check for ${v.contractor.company} for 2 days.`,
    lines: ["Check in with the sponsor and the contractor."],
    rows: [
      { label: "Contractor", value: whoLine(v) },
      { label: "Phone", value: v.contractor.phone },
      { label: "Sponsor", value: `${v.sponsorName} · ${v.sponsorEmail}` },
      { label: "No answer since", value: `Day ${fromDay}` },
      { label: "Visit", value: v.visitId },
    ],
    button: { label: "Open The Visit", path: `#/contractors/r/${encodeURIComponent(v.visitId)}` },
    footer: `You get this because you are an Alerts contact at ${site}. ${signOf(world)}`,
  });
  return { key: `cv-missed-${v.visitId}-from${fromDay}`, emails: [e] };
}

// ---- What the contractor's page sees ----

export interface ContractorView {
  visitId: string;
  status: VisitStatus;
  siteName: string;
  sponsorEmail: string;
  sponsorName: string;
  expiresAt: string;
  jobDays: number;
  endsOn: string | null;
  phone: string;
}

export function contractorViewOf(v: VisitRow, siteName: string, nowIso: string): ContractorView {
  return {
    visitId: v.visitId,
    status: visitStatusOf(v, nowIso),
    siteName,
    sponsorEmail: v.sponsorEmail,
    sponsorName: v.sponsorName,
    expiresAt: v.expiresAt,
    jobDays: v.jobDays,
    endsOn: v.endsOn,
    phone: v.contractor.phone,
  };
}

// ---- What the sponsor's page sees ----

export interface SponsorView {
  kind: "confirm" | "daily";
  visitId: string;
  status: VisitStatus;
  siteName: string;
  companyName: string;
  contractor: VisitRow["contractor"];
  checkLine: string;
  jobDays: number;
  startOn: string | null;
  endsOn: string | null;
  sponsorEmail: string;
  sponsorName: string;
  confirmedAt: string | null;
  expiresAt: string;
  /** The daily link's day. */
  day: { day: number; answer: FollowUpAnswer; note: string; at: string | null } | null;
  canExtend: boolean;
}

export function checkLineOf(v: Pick<VisitRow, "check">): string {
  return `Passed 5 of 5 at ${clockText(v.check.passedAt)}${v.check.misses ? `, ${v.check.misses} ${v.check.misses === 1 ? "miss" : "misses"} on the way` : ""}`;
}

export function sponsorViewOf(v: VisitRow, kind: SponsorView["kind"], day: number | null, siteName: string, companyName: string, nowIso: string): SponsorView {
  const f = day === null ? null : v.followUps.find((x) => x.day === day) ?? null;
  return {
    kind,
    visitId: v.visitId,
    status: visitStatusOf(v, nowIso),
    siteName,
    companyName,
    contractor: v.contractor,
    checkLine: checkLineOf(v),
    jobDays: v.jobDays,
    startOn: v.startOn,
    endsOn: v.endsOn,
    sponsorEmail: v.sponsorEmail,
    sponsorName: v.sponsorName,
    confirmedAt: v.confirmedAt,
    expiresAt: v.expiresAt,
    day: f ? { day: f.day, answer: f.answer, note: f.note, at: f.at } : null,
    canExtend: v.status === "On Site" && v.jobDays < MAX_JOB_DAYS,
  };
}

// ---- The admin list ----

const STATUS_RANK: Record<VisitStatus, number> = { Waiting: 0, "On Site": 1, Done: 2, "Not Mine": 3, Expired: 4 };

export function visitRowsFor(all: readonly VisitRow[], site: string, search: string, nowIso: string): VisitRow[] {
  const q = search.trim().toLowerCase();
  return all
    .filter((v) => site === "all" || v.siteNumber === site)
    .filter((v) => !q || `${v.contractor.name} ${v.contractor.company} ${v.sponsorEmail} ${v.sponsorName} ${v.visitId}`.toLowerCase().includes(q))
    .sort((a, b) => {
      const ra = STATUS_RANK[visitStatusOf(a, nowIso)];
      const rb = STATUS_RANK[visitStatusOf(b, nowIso)];
      const active = (r: number) => (r <= 1 ? 0 : 1);
      if (active(ra) !== active(rb)) return active(ra) - active(rb);
      if (unseenYes(a).length !== unseenYes(b).length) return unseenYes(b).length - unseenYes(a).length;
      if (ra !== rb && active(ra) === 0) return ra - rb;
      return b.createdAt.localeCompare(a.createdAt);
    });
}

export function visitTileOf(all: readonly VisitRow[], nowIso: string): { line: string; alert: number } {
  if (!all.length) return { line: "None yet", alert: 0 };
  const onSite = all.filter((v) => visitStatusOf(v, nowIso) === "On Site").length;
  const waiting = all.filter((v) => visitStatusOf(v, nowIso) === "Waiting").length;
  const yes = all.filter((v) => unseenYes(v).length > 0).length;
  const parts = [`${onSite} on site`];
  if (waiting) parts.push(`${waiting} waiting`);
  if (yes) parts.push(`${yes} Yes to see`);
  return { line: parts.join(" · "), alert: all.filter((v) => visitNeedsLook(v, nowIso)).length };
}

/** "day 2 of 3" while on site. */
export function dayLineOf(v: VisitRow, today: string): string {
  if (v.status !== "On Site" || !v.startOn) return "";
  const d = Math.min(Math.max(jobDayOf(v.startOn, today), 1), v.jobDays);
  return `day ${d} of ${v.jobDays}`;
}
